import { useEffect, useMemo, useState } from 'react'
import {
  CloseButton,
  Button,
  HStack,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  VStack,
  Field,
  Dialog,
  Box,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { FiAlertTriangle } from 'react-icons/fi'
import { useLegislator, useSaveLegislator } from '../../hooks/useLobbyDay'
import { useConstituentCounts, useGeoDistricts } from '../../hooks/useGeoDistricts'
import { ResponsiveModal } from '../shared'
import { MarkdownEditor } from './MarkdownEditor'
import { DistrictCombobox } from './DistrictCombobox'
import { districtUnitTypeFor, nearMisses } from './districtCatalog'
import { LegislatorNotesPanel } from './LegislatorNotesPanel'
import { LegislatorReadOnlyView } from './LegislatorReadOnlyView'
import type { LegislatorDto, SaveLegislatorRequest } from '../../types/lobbyDay'

interface LegislatorFormModalProps {
  isOpen: boolean
  onClose: () => void
  legislator?: LegislatorDto
  legislatorId?: string
}

const EMPTY_VALUES: SaveLegislatorRequest = {
  firstName: '', lastName: '', chamber: 'StateSenate', district: '', state: '',
  party: '', officeRoom: '', phone: '', email: '', background: '', photoUrl: '',
}

export function LegislatorFormModal({ isOpen, onClose, legislator, legislatorId }: LegislatorFormModalProps) {
  const saveMutation = useSaveLegislator()

  const { data: fetched, isLoading: isFetching } =
    useLegislator(legislatorId, isOpen && !legislator && !!legislatorId)
  const resolved = legislator ?? fetched
  const isEdit = !!resolved
  const isCreate = !resolved && !legislatorId
  const [editing, setEditing] = useState(false)
  const readOnly = !!resolved && resolved.source === 'LegiScan' && !editing
  // LWFS-5: an open-by-id fetch that comes back empty shows a "Couldn't load" body; the header
  // must not still read "Edit Legislator" over it (there is nothing being edited).
  const loadFailed = !!legislatorId && !resolved && !isFetching

  // reset the edit opt-in each time the modal opens
  useEffect(() => { if (isOpen) setEditing(false) }, [isOpen])

  const { register, control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } =
    useForm<SaveLegislatorRequest>({ defaultValues: EMPTY_VALUES })

  const chamber = useWatch({ control, name: 'chamber' })
  const state = useWatch({ control, name: 'state' })
  const district = useWatch({ control, name: 'district' })

  // US senators are statewide — the server already exempts them from the district requirement
  // (LegislatorController.Validate:196). Suppression must be a POSITIVE USSenate determination:
  // `useWatch` can report `undefined` for a beat before react-hook-form's defaultValues settle,
  // and `districtUnitTypeFor(undefined)` would resolve to `null` via its Record lookup, which
  // would misread "don't know yet" as "USSenate" and hide the field the first test asserts is
  // present. Comparing directly to the literal treats anything that isn't a confirmed USSenate
  // as district-bearing, never the reverse.
  const needsDistrict = chamber !== 'USSenate'

  useEffect(() => {
    if (!needsDistrict && district) setValue('district', '')
  }, [needsDistrict, district, setValue])

  const probes = useMemo(
    () => (needsDistrict && district?.trim() && state?.trim().length === 2
      ? [{ chamber, district: district.trim(), state: state.trim().toUpperCase() }]
      : []),
    [needsDistrict, chamber, district, state],
  )
  const { data: counts, error: countsError } = useConstituentCounts(probes, probes.length > 0)
  // Match on the probed district, not position: correct today (one probe, one row) but silently
  // wrong the moment a second probe is added, since ConstituentCountRow echoes the probe rather
  // than guaranteeing response order matches request order.
  const constituentCount = probes.length > 0
    ? counts?.find((c) => c.district === probes[0].district)?.count
    : undefined

  const { data: catalog } = useGeoDistricts(districtUnitTypeFor(chamber), state ?? '')
  const suggestions = useMemo(
    () => (constituentCount === 0 ? nearMisses(catalog ?? [], district ?? '') : []),
    [constituentCount, catalog, district],
  )

  useEffect(() => {
    if (isOpen) {
      reset(resolved
        ? {
            firstName: resolved.firstName, lastName: resolved.lastName, chamber: resolved.chamber,
            district: resolved.district, state: resolved.state, party: resolved.party ?? '',
            officeRoom: resolved.officeRoom ?? '', phone: resolved.phone ?? '', email: resolved.email ?? '',
            background: resolved.background ?? '', photoUrl: resolved.photoUrl ?? '',
          }
        : EMPTY_VALUES)
    }
  }, [isOpen, resolved, reset])

  const onSubmit = handleSubmit(async (values) => {
    const payload: SaveLegislatorRequest = {
      ...values,
      state: values.state.toUpperCase(),
      party: values.party || null,
      officeRoom: values.officeRoom || null,
      phone: values.phone || null,
      email: values.email || null,
      background: values.background || null,
      photoUrl: values.photoUrl || null,
    }
    try {
      await saveMutation.mutateAsync({ id: resolved?.id, data: payload })
      toaster.create({ type: 'success', title: isEdit ? 'Legislator updated' : 'Legislator created' })
      onClose()
    } catch {
      toaster.create({ type: 'error', title: isEdit ? 'Failed to update legislator' : 'Failed to create legislator' })
    }
  })

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="xl">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{isCreate ? 'New Legislator' : (readOnly || loadFailed) ? 'Legislator' : 'Edit Legislator'}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch">
              {isFetching ? (
                <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
              ) : legislatorId && !resolved ? (
                <VStack py={12}><Text color="fg.muted">Couldn't load this legislator.</Text></VStack>
              ) : readOnly ? (
                <LegislatorReadOnlyView legislator={resolved!} onEdit={() => setEditing(true)} />
              ) : (
                <form id="legislator-form" onSubmit={onSubmit}>
                  <VStack gap={4}>
                <SimpleGrid w="full" columns={{ base: 1, md: 2 }} gap={4}>
                  <Field.Root invalid={!!errors.firstName} required>
                    <Field.Label>First name</Field.Label>
                    <Input {...register('firstName', { required: 'First name is required' })} />
                    <Field.ErrorText>{errors.firstName?.message}</Field.ErrorText>
                  </Field.Root>
                  <Field.Root invalid={!!errors.lastName} required>
                    <Field.Label>Last name</Field.Label>
                    <Input {...register('lastName', { required: 'Last name is required' })} />
                    <Field.ErrorText>{errors.lastName?.message}</Field.ErrorText>
                  </Field.Root>
                </SimpleGrid>
                <SimpleGrid w="full" columns={{ base: 1, md: 3 }} gap={4}>
                  <Field.Root required>
                    <Field.Label>Chamber</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field {...register('chamber')}>
                        <option value="StateSenate">State Senate</option>
                        <option value="StateHouse">State House</option>
                        <option value="USHouse">US House</option>
                        <option value="USSenate">US Senate</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  {needsDistrict ? (
                    <Field.Root invalid={!!errors.district} required flex={1}>
                      <Field.Label htmlFor="legislator-district">District</Field.Label>
                      <Controller
                        name="district"
                        control={control}
                        rules={{ required: 'District is required' }}
                        render={({ field }) => (
                          <DistrictCombobox
                            chamber={chamber}
                            state={state ?? ''}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <Field.ErrorText>{errors.district?.message}</Field.ErrorText>
                    </Field.Root>
                  ) : (
                    <Field.Root flex={1}>
                      <Field.Label>District</Field.Label>
                      <Text fontSize="sm" color="gray.600" py={2}>
                        US senators are elected statewide — no district.
                      </Text>
                    </Field.Root>
                  )}
                  <Field.Root w="full" invalid={!!errors.state} required>
                    <Field.Label>State</Field.Label>
                    <Input {...register('state', {
                      required: 'State is required',
                      minLength: { value: 2, message: '2-char code' },
                    })} maxLength={2} placeholder="MA" />
                    <Field.ErrorText>{errors.state?.message}</Field.ErrorText>
                  </Field.Root>
                </SimpleGrid>
                {needsDistrict && constituentCount === 0 && (
                  <Box
                    w="full" bg="orange.50" borderWidth="1px" borderColor="orange.300"
                    borderRadius="md" p={3}>
                    <HStack gap={2} align="flex-start">
                      <Box color="orange.600" pt="2px"><FiAlertTriangle /></Box>
                      <Box>
                        <Text fontSize="sm" color="orange.900" fontWeight="medium">
                          No constituents match this district.
                        </Text>
                        <Text fontSize="xs" color="orange.800">
                          Nobody in your contacts is registered in "{district}" for this chamber,
                          so gap-fill and coverage will both show this legislator as empty. You can
                          still save — check the spelling against how the state writes it.
                        </Text>
                        {suggestions.length > 0 && (
                          <Text fontSize="xs" color="orange.800" mt={1}>
                            Did you mean:{' '}
                            {suggestions.map((s) => s.name).join(' · ')}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                  </Box>
                )}
                {needsDistrict && !!constituentCount && constituentCount > 0 && (
                  <Text w="full" fontSize="xs" color="green.700">
                    {constituentCount} constituents in your contacts.
                  </Text>
                )}
                {needsDistrict && !!countsError && (
                  <Text w="full" fontSize="xs" color="orange.700">
                    District coverage check unavailable — you can still save.
                  </Text>
                )}
                <SimpleGrid w="full" columns={{ base: 1, md: 2 }} gap={4}>
                  <Field.Root>
                    <Field.Label>Party</Field.Label>
                    <Input {...register('party')} placeholder="Democrat" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Office / room</Field.Label>
                    <Input {...register('officeRoom')} placeholder="312-B" />
                  </Field.Root>
                </SimpleGrid>
                <SimpleGrid w="full" columns={{ base: 1, md: 2 }} gap={4}>
                  <Field.Root>
                    <Field.Label>Phone</Field.Label>
                    <Input {...register('phone')} placeholder="(617) 722-1572" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Email</Field.Label>
                    <Input type="email" {...register('email')} placeholder="Mike.Barrett@masenate.gov" />
                  </Field.Root>
                </SimpleGrid>
                <Field.Root>
                  <Field.Label>Photo URL</Field.Label>
                  <Input {...register('photoUrl')} placeholder="https://…" />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Background</Field.Label>
                  <Controller
                    name="background"
                    control={control}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder="Committee assignments, priorities, past votes on our issues…"
                        rows={6}
                      />
                    )}
                  />
                </Field.Root>
                  </VStack>
                </form>
              )}
              {isEdit && resolved && !isFetching && (
                <Box w="full" borderTopWidth="1px" borderColor="border" pt={4}>
                  <Text fontSize="sm" fontWeight="medium" color="ink" mb={2}>
                    Running Notes
                  </Text>
                  <LegislatorNotesPanel legislatorId={resolved.id} />
                </Box>
              )}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            {readOnly || isFetching || (!!legislatorId && !resolved) ? (
              <Button variant="ghost" onClick={onClose}>Close</Button>
            ) : (
              <>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                <Button colorPalette="brand" type="submit" form="legislator-form" loading={isSubmitting}>
                  {isCreate ? 'Create' : 'Save'}
                </Button>
              </>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
